import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { postsApi } from '../../api/posts';
import { useAuth } from '../../auth/useAuth';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { useLanguage } from '../../i18n/useLanguage';

interface DeletePostButtonProps {
  postId: string;
  authorId: string;
  onDeleted?: () => void;
  variant?: 'text' | 'button';
  label?: string;
  className?: string;
}

export const DeletePostButton: React.FC<DeletePostButtonProps> = ({
  postId,
  authorId,
  onDeleted,
  variant = 'text',
  label,
  className,
}) => {
  const { t } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAuthor = isAuthenticated && user?.sub === authorId;
  if (!isAuthor) return null;

  const handleDelete = async () => {
    setLoading(true);
    setError(null);
    try {
      await postsApi.deletePost(postId);
      setShowModal(false);
      if (onDeleted) {
        onDeleted();
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {variant === 'text' ? (
        <button
          type="button"
          className={`delete-post-link ${label ? 'delete-post-link--labeled' : ''} ${className ?? ''}`.trim()}
          onClick={() => setShowModal(true)}
          aria-label={t('post.actionDelete')}
          title={t('post.actionDelete')}
        >
          <svg
            className="delete-post-icon"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
          {label && <span>{label}</span>}
        </button>
      ) : (
        <Button variant="danger" size="small" onClick={() => setShowModal(true)} className={className}>
          {t('post.actionDelete')}
        </Button>
      )}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setError(null); }}
        title={t('post.confirmDeleteTitle')}
        size="small"
      >
        <div className="moderation-modal">
          <p>{t('post.confirmDeleteText')}</p>
          {error && <div className="error-message">{error}</div>}
          <div className="modal-actions">
            <Button variant="outline" onClick={() => { setShowModal(false); setError(null); }} disabled={loading}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={loading}>
              {loading ? '…' : t('post.actionDelete')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
